import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Chip,
  Box,
  CircularProgress,
  Button
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SecurityIcon from "@mui/icons-material/Security";
import DownloadIcon from "@mui/icons-material/Download"; 
import NoAccountsIcon from "@mui/icons-material/NoAccounts";
import * as XLSX from "xlsx"; 
import API from "../api";

// ====== Static Theme Mapping for Audit Action Event Chips ======
const CHIP_COLOR_MAP = {
  SUCCESS: { color: "success", variant: "outlined", style: {} },
  CREATED: { color: "success", variant: "outlined", style: {} },
  FAILED: { color: "error", variant: "filled", style: {} },
  BLOCKED: { color: "error", variant: "filled", style: {} },
  LEAVE: { color: "warning", variant: "filled", style: {} },
  DEACTIVATE: { color: "warning", variant: "filled", style: {} },
  RESET: { color: "secondary", variant: "filled", style: { fontWeight: "bold" } },
  DELETE: { color: "secondary", variant: "filled", style: { fontWeight: "bold" } },
  PURGE: { color: "secondary", variant: "filled", style: { fontWeight: "bold" } },
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasClearance, setHasClearance] = useState(true);

  // 🔒 Memoized Action Event Parser (Improves rendering performance under long list loads)
  const renderActionChip = useCallback((action) => {
    const act = String(action).toUpperCase();
    const matchedKey = Object.keys(CHIP_COLOR_MAP).find((key) => act.includes(key));
    
    if (matchedKey) {
      const config = CHIP_COLOR_MAP[matchedKey];
      return <Chip label={action} color={config.color} variant={config.variant} size="small" sx={config.style} />;
    }
    return <Chip label={action} size="small" variant="outlined" />;
  }, []);

  // 🚀 Fetch Audit Ledgers Hook
  const fetchAuditLogs = useCallback(async () => {
    setLoading(true);
    try {
      // 🚀 FIXED: Pointing straight to the explicit endpoint path without extra trailing redirect slashes
      const res = await API.get("/users/admin/audit-logs");
      setLogs(Array.isArray(res.data) ? res.data : []);
      setHasClearance(true);
    } catch (err) {
      console.error("Audit log retrieval failure:", err);
      const status = err.response?.status;
      if (status === 403 || status === 401) {
        setHasClearance(false);
      } else {
        setError(err.response?.data?.detail || "Network communication fault while trying to access system ledgers.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  // 📊 Excel Compilation Engine
  const exportToExcel = () => {
    if (!logs || logs.length === 0) {
      setError("There are no record rows available inside the ledger view to export.");
      return;
    }

    // Map raw data arrays cleanly into formal spreadsheet cell structures
    const recordsToExport = logs.map((log) => ({
      "Timestamp Trace": new Date(log.timestamp).toLocaleString(),
      "Trigger Action Event": log.action,
      "Operator Profile ID": log.operator,
      "Client Host IP Address": log.ip,
      "Transaction Summary Context Details": log.details || "N/A"
    }));

    // Generate SheetJS Worksheet structures
    const worksheet = XLSX.utils.json_to_sheet(recordsToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Security Trace Index");

    // Dynamic Column Auto-width Calculator with standard safe padding caps
    if (recordsToExport.length > 0) {
      const maxColumnWidths = Object.keys(recordsToExport[0]).map((key) => {
        const longestRowValue = recordsToExport.reduce((max, obj) => {
          const valLen = obj[key] ? String(obj[key]).length : 0;
          return Math.max(max, valLen);
        }, key.length);
        // Cap cell max width calculation parameters at 50 chars to avoid oversized description cells
        return { wch: Math.min(Math.max(longestRowValue + 3, 12), 50) }; 
      });
      worksheet["!cols"] = maxColumnWidths;
    }

    // Trigger file download
    const fileTimestamp = new Date().toISOString().split("T")[0];
    XLSX.writeFile(workbook, `System_Security_Audit_Trail_${fileTimestamp}.xlsx`);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      
      {/* 🚀 THE FIX: Move styling properties inside the sx prop wrapper layout mapping container */}
      <Box 
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 4
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <SecurityIcon color="primary" fontSize="large" />
          <Typography variant="h4" component="h1" fontWeight="600">
            System Security Audit Trail
          </Typography>
        </Box>
        
        <Box sx={{ display: "flex", gap: 2, width: { xs: "100%", sm: "auto" } }}>
          <Button
            variant="outlined"
            color="success"
            fullWidth={window.innerWidth < 600}
            startIcon={<DownloadIcon />}
            disabled={loading || !hasClearance || logs.length === 0}
            onClick={exportToExcel}
          >
            Export to Excel
          </Button>
          <Button
            variant="contained"
            fullWidth={window.innerWidth < 600}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            disabled={loading || !hasClearance}
            onClick={fetchAuditLogs}
          >
            Refresh Logs
          </Button>
        </Box>
      </Box>

      {/* Main Ledger Content Render Core */}
      {!hasClearance ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: "center", border: "1px solid rgba(211, 47, 47, 0.2)", bgcolor: "rgba(211, 47, 47, 0.01)" }}>
          <NoAccountsIcon color="error" sx={{ fontSize: 54, mb: 2 }} />
          <Typography variant="h6" color="error.main" fontWeight="600" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" maxWidth={500} mx="auto">
            Your assigned account credentials lack adequate administrative clearance privileges to view corporate ledger indices.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Table aria-label="system audit ledger database table">
            <TableHead sx={{ backgroundColor: "action.hover" }}>
              <TableRow>
                <TableCell style={{ width: "18%", fontWeight: "700" }}>Timestamp</TableCell>
                <TableCell style={{ width: "20%", fontWeight: "700" }}>Operational Trigger Event</TableCell>
                <TableCell style={{ width: "15%", fontWeight: "700" }}>Operator Profile</TableCell>
                <TableCell style={{ width: "14%", fontWeight: "700" }}>Client Host IP</TableCell>
                <TableCell style={{ width: "33%", fontWeight: "700" }}>Transaction Context / Metadata Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && logs.length === 0 ? (
                <TableRow>
                  {/* 🚀 THE FIX: Converted raw unrecognized <colSpan> tags to clean <TableCell> configurations */}
                  <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={40} thickness={4} />
                    <Typography variant="body2" sx={{ mt: 1.5, color: "text.secondary", fontWeight: 500 }}>
                      Querying secure ledger indices...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  {/* 🚀 THE FIX: Converted raw unrecognized <colSpan> tags to clean <TableCell> configurations */}
                  <TableCell colSpan={5} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No security events or operational modifications recorded in the log history.
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow 
                    key={log.id} 
                    sx={{ 
                      "&:hover": { backgroundColor: "action.hover" },
                      transition: "background-color 0.15s ease-in-out"
                    }}
                  >
                    <TableCell sx={{ color: "text.secondary" }}>
                      {new Date(log.timestamp).toLocaleString(undefined, {
                        dateStyle: "short",
                        timeStyle: "medium"
                      })}
                    </TableCell>
                    <TableCell>{renderActionChip(log.action)}</TableCell>
                    <TableCell sx={{ fontWeight: "500" }}>{log.operator}</TableCell>
                    <TableCell>
                      <Chip label={log.ip || "Unknown IP"} size="small" variant="outlined" sx={{ fontFamily: "monospace", letterSpacing: "0.5px" }} />
                    </TableCell>
                    <TableCell sx={{ color: "text.primary", fontSize: "0.875rem", wordBreak: "break-word" }}>
                      {log.details}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Global Notification Monitor Toast */}
      <Snackbar
        open={!!error}
        message={error}
        autoHideDuration={4000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
}